import type { RequestPayload } from "@inertiajs/core";
import { Alert, Checkbox, Fieldset, Stack, Tabs, Text } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import { IconAlertTriangle } from "@tabler/icons-react";
import { type PropsWithChildren, type ReactNode, useState } from "react";
import { renderEmailPreview } from "../../../helpers/emailPreviewHelper";
import type { Group } from "../../../models/group";
import type { YearContext } from "../../../models/yearContext";
import { EmailEditorForm, type EmailFormType, PlaceholderGroup } from "../../email/EmailEditorForm";
import { CommonConfigForm, type CommonConfigFormErrorType, type CommonConfigFormType } from "./CommonConfigForm";

type BaseConfigFormProps<T extends BaseConfigFormType> = PropsWithChildren<{
  form: UseFormReturnType<T>;
  onSubmit: (value: RequestPayload) => void;
  saveButtonLabel: string;
  errors?: CommonConfigFormErrorType & {
    data?: string[];
  };
  yearContexts: YearContext[];
  groups: Group[];
  alert?: ReactNode;
}>;

export type BaseConfigFormType = EmailFormType &
  CommonConfigFormType & {
    send_mail: boolean;
  };

export function BaseConfigForm<T extends BaseConfigFormType>({
  form,
  onSubmit,
  saveButtonLabel,
  children,
  errors,
  alert,
  ...props
}: BaseConfigFormProps<T>) {
  const [previewText, setPreviewText] = useState("");
  const onPreview = () => setPreviewText(renderEmailPreview(form.getValues().email_template));
  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack>
        {alert}
        {errors?.data && (
          <Alert maw="60rem" variant="light" icon={<IconAlertTriangle />} title="Struktureller Fehler" color="red">
            <Text>{errors.data}</Text>
          </Alert>
        )}
        <Tabs defaultValue="data">
          <Tabs.List mb="md">
            <Tabs.Tab
              value="data"
              styles={{
                tabLabel: {
                  color: Object.keys(form.errors).some((key) => key !== "email_template" && key !== "subject") ? "red" : undefined,
                },
              }}
            >
              Daten
            </Tabs.Tab>
            <Tabs.Tab
              value="email"
              styles={{
                tabLabel: { color: form.errors?.subject || form.errors?.email_template ? "red" : undefined },
              }}
            >
              E-Mail
            </Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="data">
            <Fieldset legend="Details">
              <Stack maw="60rem">
                <CommonConfigForm form={form} errors={errors} {...props} />
                {children}
              </Stack>
            </Fieldset>
          </Tabs.Panel>
          <Tabs.Panel value="email">
            <EmailEditorForm
              form={form}
              previewText={previewText}
              onPreview={onPreview}
              saveButtonLabel={saveButtonLabel}
              required={form.getValues().send_mail}
              showPlaceholderGroups={
                PlaceholderGroup.Apprentice | PlaceholderGroup.Group | PlaceholderGroup.Receiver | PlaceholderGroup.Document
              }
            >
              <Checkbox label="E-Mail senden" key={form.key("send_mail")} {...form.getInputProps("send_mail", { type: "checkbox" })} />
            </EmailEditorForm>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </form>
  );
}
